import React, { useMemo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { translateLocation } from '../../utils/translationUtils';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data } = useData();
  const { selectedLocation } = useOutletContext() || {};

  const locationFilter = useMemo(() => {
    if (user?.role === 'manager') return (user.location || '').toLowerCase();
    if (selectedLocation && selectedLocation !== 'all') return selectedLocation.toLowerCase();
    return '';
  }, [selectedLocation, user?.location, user?.role]);

  const visibleUsers = useMemo(() => {
    const list = data.users || [];
    if (!locationFilter) return list;
    return list.filter((u) => (u.location || '').toLowerCase() === locationFilter);
  }, [data.users, locationFilter]);

  const visibleCourses = useMemo(() => {
    const list = data.courses || [];
    if (!locationFilter) return list;
    return list.filter((c) => (c.location || '').toLowerCase() === locationFilter);
  }, [data.courses, locationFilter]);

  const visibleEnrollments = useMemo(() => {
    const list = data.enrollments || [];
    if (!locationFilter) return list;
    return list.filter((e) => (e.course?.location || '').toLowerCase() === locationFilter);
  }, [data.enrollments, locationFilter]);

  const stats = {
    users: visibleUsers.length,
    courses: visibleCourses.length,
    enrollments: visibleEnrollments.length,
    studies: (data.studies || []).length
  };

  const roleCapabilities = useMemo(() => {
    if (user?.role === 'admin') {
      return [
        t('admin.capabilities.admin.1'),
        t('admin.capabilities.admin.2'),
        t('admin.capabilities.admin.3')
      ];
    }

    if (user?.role === 'manager') {
      return [
        t('admin.capabilities.manager.1'),
        t('admin.capabilities.manager.2'),
        t('admin.capabilities.manager.3')
      ];
    }

    return [
      t('admin.capabilities.instructor.1'),
      t('admin.capabilities.instructor.2'),
      t('admin.capabilities.instructor.3')
    ];
  }, [t, user?.role]);

  const menuItems = useMemo(() => {
    if (user?.role === 'admin') {
      return [
        { key: 'users', title: t('admin.manageUsers'), path: '/admin/users' },
        { key: 'studies', title: t('admin.manageStudies'), path: '/admin/studies' },
        { key: 'enrollments', title: t('admin.manageEnrollments'), path: '/admin/enrollments' },
        { key: 'payments', title: t('admin.payments'), path: '/admin/payments' },
        { key: 'reports', title: t('admin.reports'), path: '/admin/reports' },
        { key: 'calendar', title: t('admin.calendar'), path: '/admin/calendar' }
      ];
    }

    if (user?.role === 'manager') {
      return [
        { key: 'users', title: t('admin.manageUsers'), path: '/admin/users' },
        { key: 'studies', title: t('admin.manageStudies'), path: '/admin/studies' },
        { key: 'enrollments', title: t('admin.manageEnrollments'), path: '/admin/enrollments' },
        { key: 'payments', title: t('admin.payments'), path: '/admin/payments' },
        { key: 'homework', title: t('admin.homework'), path: '/admin/homework' },
        { key: 'calendar', title: t('admin.calendar'), path: '/admin/calendar' }
      ];
    }

    return [
      { key: 'homework', title: t('admin.homework'), path: '/admin/homework' },
      { key: 'reports', title: t('admin.reports'), path: '/admin/reports' },
      { key: 'calendar', title: t('admin.calendar'), path: '/admin/calendar' }
    ];
  }, [t, user?.role]);

  const roleLabel = t(`roles.${user?.role || 'student'}`);
  const locationLabel = user?.location ? translateLocation(t, String(user.location).toLowerCase()) : t('admin.allLocations');

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.title}>{t('admin.dashboard')}</h1>
        <p style={styles.welcome}>{t('admin.welcome', { name: user?.fullName || user?.firstName || '-' })}</p>

        <div style={styles.metaRow}>
          <span style={styles.metaPill}>{t('admin.roleLabel')}: {roleLabel}</span>
          <span style={styles.metaPill}>{t('admin.locationLabel')}: {locationLabel}</span>
        </div>

        <div style={styles.capabilityCard}>
          <h2 style={styles.sectionTitle}>{t('admin.whatYouCanManage')}</h2>
          <ul style={styles.capabilityList}>
            {roleCapabilities.map((item) => (
              <li key={item} style={styles.capabilityItem}>{item}</li>
            ))}
          </ul>
        </div>

        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <span style={styles.statNumber}>{stats.users}</span>
            <span style={styles.statLabel}>{t('admin.users')}</span>
          </div>
          <div style={styles.statCard}>
            <span style={styles.statNumber}>{stats.courses}</span>
            <span style={styles.statLabel}>{t('admin.courses')}</span>
          </div>
          <div style={styles.statCard}>
            <span style={styles.statNumber}>{stats.enrollments}</span>
            <span style={styles.statLabel}>{t('admin.enrollments')}</span>
          </div>
          <div style={styles.statCard}>
            <span style={styles.statNumber}>{stats.studies}</span>
            <span style={styles.statLabel}>{t('admin.studies')}</span>
          </div>
        </div>

        <h2 style={styles.sectionTitle}>{t('admin.quickActions')}</h2>
        <div style={styles.menuGrid}>
          {menuItems.map((item) => (
            <Link key={item.key} to={item.path} style={styles.menuCard}>
              <span style={styles.menuTitle}>{item.title}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem'
  },
  title: {
    fontSize: '2rem',
    color: '#333',
    marginBottom: '0.5rem'
  },
  welcome: {
    color: '#666',
    marginBottom: '1rem'
  },
  metaRow: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    marginBottom: '1rem'
  },
  metaPill: {
    backgroundColor: '#e7f0ff',
    color: '#1f3b64',
    borderRadius: '999px',
    fontSize: '0.85rem',
    padding: '0.35rem 0.75rem',
    fontWeight: '600'
  },
  capabilityCard: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '1.25rem',
    marginBottom: '1.5rem'
  },
  capabilityList: {
    margin: 0,
    paddingLeft: '1.1rem',
    color: '#4b5563'
  },
  capabilityItem: {
    marginBottom: '0.5rem'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.5rem',
    marginBottom: '3rem'
  },
  statCard: {
    backgroundColor: '#ffffff',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#0f766e'
  },
  statLabel: {
    color: '#666',
    fontSize: '0.875rem'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    color: '#333',
    marginBottom: '1.5rem'
  },
  menuGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem'
  },
  menuCard: {
    backgroundColor: '#ffffff',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  menuTitle: {
    fontSize: '1.1rem',
    fontWeight: '500',
    color: '#333'
  }
};

export default Dashboard;
